
import React, { useMemo, useState } from 'react';
import { CellarStats, Wine, ConsumedWine } from '../types';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

interface DashboardProps {
  stats: CellarStats;
  wines: Wine[];
  history: ConsumedWine[];
  onRemoveHistory: (id: string) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ stats, wines, history, onRemoveHistory }) => {
  const [showTimeline, setShowTimeline] = useState(false);
  const chartData = useMemo(() => 
    Object.entries(stats.byType).map(([name, value]) => ({ name, value })),
  [stats.byType]);

  const COLORS = ['#ef4444', '#facc15', '#f472b6', '#38bdf8', '#fbbf24'];
  const currentYear = new Date().getFullYear();

  const categorizedWines = useMemo(() => {
    const sorted = [...(wines || [])].sort((a, b) => {
      const getYear = (w: Wine) => {
        const m = (w.drinkingWindow || "").match(/\d{4}/);
        return m ? parseInt(m[0]) : 9999;
      };
      return getYear(a) - getYear(b);
    });

    return {
      now: sorted.filter(w => {
        const match = w.drinkingWindow?.match(/\d{4}/g);
        if (!match) return false;
        const start = parseInt(match[0]);
        const end = match[1] ? parseInt(match[1]) : start + 5;
        return currentYear >= start && currentYear <= end;
      })
    };
  }, [wines, currentYear]);

  return (
    <div className="space-y-8 pb-12">
      <div 
        onClick={() => setShowTimeline(true)}
        className="bg-amber-500/10 border border-amber-500/30 rounded-[2.5rem] p-6 cursor-pointer active:scale-95 transition-all shadow-xl group"
      >
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-amber-500 text-[10px] font-black uppercase tracking-[0.2em]">Pronti alla beva</h4>
          <span className="text-[8px] font-black text-amber-500/40 uppercase group-hover:text-amber-500 transition-colors">Vedi Cronologia →</span>
        </div>
        
        {categorizedWines.now.length > 0 ? (
          <div className="flex -space-x-3 overflow-hidden py-2">
            {categorizedWines.now.slice(0, 5).map(wine => (
              <div key={wine.id} className="inline-block h-12 w-12 rounded-full ring-2 ring-[#0a0503] bg-white overflow-hidden shadow-lg">
                <img className="h-full w-full object-contain p-1" src={wine.imageUrls[0]} alt="" />
              </div>
            ))}
            {categorizedWines.now.length > 5 && (
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-600 text-[10px] font-black ring-2 ring-[#0a0503]">
                +{categorizedWines.now.length - 5}
              </div>
            )}
          </div>
        ) : (
          <p className="text-slate-600 text-[10px] font-bold italic">Tutto in evoluzione.</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white/[0.03] p-6 rounded-[2rem] border border-white/5 shadow-xl">
          <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest mb-1">Stock</p>
          <h3 className="text-3xl font-bold text-amber-500">{Number(stats.totalBottles) || 0}</h3>
        </div>
        <div className="bg-white/[0.03] p-6 rounded-[2rem] border border-white/5 shadow-xl">
          <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest mb-1">Asset</p>
          <h3 className="text-2xl font-bold text-amber-500 leading-tight">€{(Number(stats.totalValue) || 0).toLocaleString()}</h3>
        </div>
      </div>

      <div className="bg-white/[0.02] p-6 rounded-[3rem] border border-white/5 h-[380px] shadow-2xl">
        <h4 className="text-amber-100/40 text-[10px] font-black uppercase tracking-[0.2em] mb-4 text-center">Composizione Cantina</h4>
        <ResponsiveContainer width="100%" height="90%">
          <PieChart>
            <Pie data={chartData} cx="50%" cy="45%" innerRadius={60} outerRadius={85} paddingAngle={8} dataKey="value" stroke="none">
              {chartData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
            </Pie>
            <Tooltip contentStyle={{ backgroundColor: '#0a0503', border: 'none', borderRadius: '20px' }} itemStyle={{ color: '#f59e0b', fontSize: '10px' }} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* CRONOLOGIA BOTTIGLIE STAPPATE */}
      <section className="space-y-6">
        <div className="flex items-center justify-between px-2">
          <h4 className="text-[10px] font-black text-amber-500/60 uppercase tracking-[0.3em]">Cronologia Bevute</h4>
          <span className="text-[9px] font-black text-slate-700 uppercase">{history.length} Degustazioni</span>
        </div>
        
        <div className="space-y-3">
          {history.length > 0 ? history.slice(0, 15).map((item) => (
            <div key={item.id} className="bg-white/[0.03] border border-white/5 rounded-3xl p-4 flex items-center gap-4 animate-scale-in group">
              <div className="w-12 h-16 bg-white rounded-xl overflow-hidden flex-none">
                 <img src={item.imageUrl} className="w-full h-full object-contain p-1" alt="" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-start">
                  <h5 className="text-[10px] font-black text-amber-100 uppercase truncate tracking-widest">{item.name}</h5>
                  <div className="flex items-center gap-2">
                    <span className="text-[7px] font-black text-slate-600 uppercase bg-white/5 px-2 py-0.5 rounded-full">
                      {new Date(item.consumedAt).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}
                    </span>
                    <button 
                      onClick={() => onRemoveHistory(item.id)}
                      className="p-1 text-slate-700 hover:text-red-500 transition-colors active:scale-75"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    </button>
                  </div>
                </div>
                <p className="text-[8px] text-slate-500 font-bold uppercase italic truncate">{item.producer} • {item.vintage}</p>
                <div className="flex items-center gap-2 mt-2">
                  <div className={`w-1.5 h-1.5 rounded-full ${item.type === 'Rosso' ? 'bg-red-900' : 'bg-amber-100'}`}></div>
                  <span className="text-[7px] text-slate-600 font-black uppercase tracking-widest">{item.type}</span>
                </div>
              </div>
            </div>
          )) : (
            <div className="text-center py-10 bg-white/[0.01] border border-dashed border-white/5 rounded-[2rem]">
               <p className="text-[10px] text-slate-600 font-black uppercase tracking-widest">Nessuna bottiglia stappata ancora.</p>
            </div>
          )}
        </div>
      </section>

      {showTimeline && (
        <div className="fixed inset-0 z-[100] bg-[#0a0503] flex flex-col animate-scale-in">
          <div className="p-6 pt-[calc(env(safe-area-inset-top)+1rem)] flex justify-between items-center border-b border-white/5">
            <h2 className="text-2xl font-bold text-amber-500 serif">Pianificazione Beva</h2>
            <button onClick={() => setShowTimeline(false)} className="bg-white/5 p-3 rounded-2xl text-slate-400">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {categorizedWines.now.map(wine => (
              <div key={wine.id} className="bg-white/5 rounded-3xl p-4 flex items-center gap-4">
                <div className="w-12 h-16 bg-white rounded-xl overflow-hidden flex-none">
                  <img src={wine.imageUrls[0]} className="w-full h-full object-contain p-1" alt="" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-[10px] font-black text-amber-100 uppercase truncate tracking-widest">{wine.name}</h4>
                  <p className="text-[8px] text-slate-500 font-bold italic truncate">{wine.producer}</p>
                  <span className="text-[9px] font-black text-green-400 mt-2 block">{wine.drinkingWindow}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
